import React, { useState, useEffect, useRef } from "react";

function QuiltEditor({ quiltGrid, gridSize = "2x2", onSave, onCancel, saveButtonText = "Save Changes" }) {
    const [editedGrid, setEditedGrid] = useState([...quiltGrid]); // Editable copy of the quilt grid
    const [originalGrid, setOriginalGrid] = useState([...quiltGrid]); // Original copy for reverting
    const [selectedTiles, setSelectedTiles] = useState([]); // Tracks selected tiles for swapping
    const [zoomLevel, setZoomLevel] = useState(1); // Default zoom level is 1 (100%)
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef(null);
    const [forceUpdate, setForceUpdate] = useState(Date.now()); // Add state to force re-renders

    // Parse the gridSize (e.g., "7x7") to get columns and rows
    const [columns, rows] = (gridSize || "2x2").split("x").map(Number); // Fallback to "2x2" if gridSize is missing

    // Use useEffect to measure and update container width on mount and resize
    useEffect(() => {
        const updateContainerWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth;
                setContainerWidth(width);
            }
        };

        // Initial calculation
        updateContainerWidth();

        // Update on window resize
        window.addEventListener('resize', updateContainerWidth);
        return () => window.removeEventListener('resize', updateContainerWidth);
    }, []);

    // Calculate dynamic tile size based on available container width and grid size
    const calculateTileSize = () => {
        // Use the containerWidth if available, otherwise use a responsive calculation
        const availableWidth = containerWidth || Math.min(window.innerWidth - 40, 800);
        
        // Account for gaps and borders in the calculation - increased gap size
        const gapSize = 8; // Increased from 4px
        const borderSize = 2; // 1px border on each side
        const gapAndBorderSpace = columns * (gapSize + (borderSize * 2));
        const usableWidth = availableWidth - gapAndBorderSpace;
        
        // Calculate raw tile size
        const calculatedSize = usableWidth / columns;
        
        // Dynamically adjust max tile size based on grid dimensions
        // For larger grids (more tiles), use smaller max size
        let maxTileSize;
        
        if (columns >= 10 || rows >= 10) {
            // Very large grids (10x10 or larger)
            maxTileSize = 80; 
        } else if (columns >= 8 || rows >= 8) {
            // Large grids (8x8 or 9x9)
            maxTileSize = 90;
        } else if (columns >= 6 || rows >= 6) {
            // Medium grids (6x6 or 7x7)
            maxTileSize = 100;
        } else {
            // Small grids (5x5 or smaller)
            maxTileSize = 120;
        }
        
        // For laptop screens, adjust based on screen height to ensure vertical fit
        const screenHeight = window.innerHeight;
        if (screenHeight < 900) { // Typical laptop height
            // Reduce max size proportionally to screen height
            const heightFactor = Math.min(1, screenHeight / 900);
            maxTileSize = Math.floor(maxTileSize * heightFactor);
        }
        
        return Math.min(Math.max(Math.floor(calculatedSize), 30), maxTileSize);
    };

    const tileSize = calculateTileSize();

    // Update the grid states when quiltGrid changes (for dynamic updates)
    useEffect(() => {
        // Create deep copies to avoid reference issues
        setEditedGrid(quiltGrid.map(tile => ({...tile})));
        setOriginalGrid(quiltGrid.map(tile => ({...tile})));
    }, [quiltGrid]);

    // Function to calculate optimal zoom level based on matrix dimensions
    const calculateOptimalZoom = () => {
        // Base calculation for optimal zoom
        let optimalZoom = 1.0;
        
        // Adjust zoom based on grid size for better initial display
        if (columns >= 10 || rows >= 10) {
            // Very large grids
            optimalZoom = 0.65;
        } else if (columns >= 8 || rows >= 8) {
            // Large grids
            optimalZoom = 0.75;
        } else if (columns >= 6 || rows >= 6) {
            // Medium grids
            optimalZoom = 0.85;
        }
        
        // Further adjust based on screen size
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // On smaller screens, reduce zoom more aggressively
        if (screenWidth < 1024 || screenHeight < 768) {
            optimalZoom = Math.min(optimalZoom, 0.8);
        }
        
        return optimalZoom;
    };

    // Calculate the optimal initial zoom level based on grid size
    useEffect(() => {
        // Set initial zoom level based on grid size
        const initialZoom = calculateOptimalZoom();
        setZoomLevel(initialZoom);
    }, [gridSize, columns, rows]);
    
    // Determine if scrolling is needed - only when zoomed in beyond 100%
    const needsScrolling = zoomLevel > 1;
    
    // Handle tile selection for swapping - IMPROVED IMPLEMENTATION
    const handleTileSelect = (index) => {
        if (selectedTiles.length < 2) {
            setSelectedTiles([...selectedTiles, index]);
        }

        // Swap tiles if two are selected
        if (selectedTiles.length === 1) {
            const firstIndex = selectedTiles[0];
            const secondIndex = index;
            
            // Skip if the same tile is selected twice
            if (firstIndex === secondIndex) {
                setSelectedTiles([]);
                return;
            }
            
            // Create a deep copy of the grid with completely new objects
            const newGrid = editedGrid.map(tile => ({...tile}));
            
            // Store references to the original tiles (with deep copies)
            const firstTile = JSON.parse(JSON.stringify(newGrid[firstIndex]));
            const secondTile = JSON.parse(JSON.stringify(newGrid[secondIndex]));
            
            // Store the original positions
            const firstPosition = [...firstTile.position];
            const secondPosition = [...secondTile.position];
            
            // Create completely new tile objects with swapped positions
            newGrid[firstIndex] = {
                ...secondTile,
                position: firstPosition,
                key: `swapped-${Date.now()}-${secondIndex}-at-${firstPosition.join(',')}`
            };
            
            newGrid[secondIndex] = {
                ...firstTile,
                position: secondPosition,
                key: `swapped-${Date.now()}-${firstIndex}-at-${secondPosition.join(',')}`
            };
            
            console.log("Swapping tiles:", {
                firstBefore: editedGrid[firstIndex],
                secondBefore: editedGrid[secondIndex],
                firstAfter: newGrid[firstIndex],
                secondAfter: newGrid[secondIndex]
            });
            
            // Update state with the new grid and force a re-render
            setEditedGrid(newGrid);
            setForceUpdate(Date.now());
            setSelectedTiles([]); // Reset selected tiles
        }
    };

    // Handle zoom in
    const handleZoomIn = () => {
        setZoomLevel(prevZoom => Math.min(prevZoom + 0.25, 3)); // Increase zoom by 25%, max 300%
    };

    // Handle zoom out
    const handleZoomOut = () => {
        setZoomLevel(prevZoom => Math.max(prevZoom - 0.25, 0.5)); // Decrease zoom by 25%, min 50%
    };

    // Reset zoom to default (100%)
    const handleResetZoom = () => {
        setZoomLevel(1);
    };

    // Calculate the actual tile size after zoom applied
    const zoomedTileSize = tileSize * zoomLevel;
    
    // Determine if vertical scrolling is needed based on zoom level
    const needsVerticalScrolling = zoomLevel > 1;

    // Save the modified quilt layout
    const handleSave = () => {
        // Create a proper copy with updated positions to ensure React detects changes
        const updatedGrid = editedGrid.map(tile => {
            // Generate unique identifier for each tile
            const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            return {
                ...tile,
                key: uniqueId
            };
        });
        
        console.log("Saving updated grid:", updatedGrid);
        onSave(updatedGrid); // Pass the edited grid back to parent for saving
    };

    // Cancel changes and revert to the original layout
    const handleCancel = () => {
        setEditedGrid(originalGrid.map(tile => ({...tile}))); // Revert back to the original layout
        if (onCancel) onCancel(); // Trigger onCancel if provided
    };

    return (
        <div className="bg-white p-3 md:p-6 rounded-lg shadow-lg max-w-4xl w-full mx-auto overflow-hidden" ref={containerRef}>
            <h2 className="text-lg md:text-xl font-bold mb-2 md:mb-4 text-center" style={{ fontFamily: "Playfair Display" }}>
                Edit Quilt Layout
            </h2>
            
            <p className="mb-3 md:mb-4 text-gray-600 text-center text-xs md:text-sm">
                Click on two tiles to swap their positions. Use this to rearrange your quilt layout.
            </p>
            
            {/* Zoom controls */}
            <div className="flex justify-center mb-3 md:mb-4">
                <div className="bg-gray-100 rounded-lg p-1 inline-flex">
                    <button 
                        onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.1))}
                        className="px-2 py-1 text-gray-700 hover:bg-gray-200 rounded"
                        disabled={zoomLevel <= 0.5}
                    >
                        −
                    </button>
                    <span className="px-3 py-1 text-gray-700 text-sm">Zoom: {Math.round(zoomLevel * 100)}%</span>
                    <button 
                        onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.1))}
                        className="px-2 py-1 text-gray-700 hover:bg-gray-200 rounded"
                        disabled={zoomLevel >= 2}
                    >
                        +
                    </button>
                </div>
            </div>
            
            {/* Centered quilt grid container - with improved overflow handling */}
            <div 
                className="overflow-auto mx-auto" 
                style={{ 
                    maxHeight: `${Math.min(600, window.innerHeight - 250)}px`,
                    padding: '8px'
                }}
            >
                <div 
                    className="grid mx-auto"
                    style={{ 
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                        gap: '8px', // Increased gap size from 4px to 8px
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: 'center top',
                        margin: '0 auto',  // This helps with centering
                        width: 'fit-content' // Ensure the grid container fits its content
                    }}
                >
                    {editedGrid.map((tile, index) => {
                        // Generate unique key that changes when tiles are swapped
                        const uniqueKey = `tile-${index}-${tile?.url || 'empty'}-${forceUpdate}`;
                        const isSelected = selectedTiles.includes(index);
                        
                        return (
                            <div
                                key={uniqueKey}
                                className={`transition-all duration-200 relative ${
                                    isSelected
                                        ? "border-4 border-blue-500 shadow-lg"
                                        : "border-2 border-gray-300 hover:border-blue-500"
                                }`}
                                onClick={() => handleTileSelect(index)}
                                style={{
                                    width: `${tileSize}px`,
                                    height: `${tileSize}px`,
                                    boxSizing: "border-box", // Ensure borders don't change dimensions
                                    borderRadius: "4px", // Slightly increased border radius
                                    overflow: 'hidden', // Prevent content from overflowing
                                    transform: isSelected ? 'scale(1.03)' : 'scale(1)', // Slightly larger when selected
                                    zIndex: isSelected ? 10 : 1, // Bring selected tiles to front
                                }}
                            >
                                {/* Selection indicator */}
                                {isSelected && (
                                    <div className="absolute inset-0 border-4 border-blue-500 bg-blue-100 bg-opacity-30 z-10 pointer-events-none">
                                        <div className="absolute top-1 right-1 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                            {selectedTiles.indexOf(index) + 1}
                                        </div>
                                    </div>
                                )}
                                
                                {tile?.url ? (
                                    <img
                                        src={tile.url}
                                        alt={`Tile ${index}`}
                                        className={`w-full h-full object-cover ${isSelected ? 'brightness-105' : ''}`}
                                        style={{ borderRadius: "1px" }} // Smaller border radius for image
                                        key={`img-${tile.url}-${index}-${forceUpdate}`}
                                    />
                                ) : (
                                    <div className={`w-full h-full bg-gray-200 flex items-center justify-center text-xs ${isSelected ? 'bg-gray-300' : ''}`}
                                         style={{ borderRadius: "1px" }}>
                                        Empty
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {/* Action buttons - centered, with smaller padding for better space usage */}
            <div className="flex justify-center mt-3 md:mt-4 space-x-4">
                <button
                    onClick={handleCancel}
                    className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-300 hover:bg-gray-400 rounded-lg text-sm"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="order-1 sm:order-2 w-full sm:w-auto px-4 py-2 bg-[#9CAF88] hover:bg-[#8A9B79] text-white rounded-lg flex items-center justify-center sm:justify-end" 
                    // Changed from indigo-600/700 to sage green colors
                >
                    {saveButtonText}
                </button>
            </div>
        </div>
    );
}

export default QuiltEditor;